import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TreeViewComponent } from '@syncfusion/ej2-react-navigations'
import PanelBarGLPure from '../PanelBarGLPure/panelBarGL-pure'
import DropDownBtnHLPure from '../dropDownHL/dropDownHL.pure'
import ButtonAddHLPure from '../buttonAddHL/buttonAddHL.pure'
import ComboBoxTemplatePure from '../comboBox-template/comboBox-template.pure'
import DialogYesNoHLPure from '../dialogYesNoHL/dialogYesNoHL.pure'
import InputHLPure from '../inputHL/inputHL.pure'
import './classification-panel.css'
import SearchBarHLPure from '../searchBarHL/searchBarHL.pure'
import { canCreateSubcategory as canCreateSubcategoryByRole } from '../../../utils/roles.util'

/**
 * ClassificationPanelPure - Componente reutilizable para clasificación de documentos
 * 
 * @param {Object} props
 * @param {Array} props.companies - Lista de empresas
 * @param {string|number} props.companyId - ID de la empresa seleccionada
 * @param {Function} props.onCompanyChange - Callback cuando cambia la empresa
 * @param {boolean} props.companyDisabled - Deshabilitar selector de empresa
 * @param {boolean} props.showCompanyError - Mostrar error de empresa
 * @param {string} props.companyErrorMessage - Mensaje de error de empresa
 * @param {boolean} props.requireCompany - Si la empresa es requerida (default: false)
 * 
 * @param {Array} props.categoriesData - Datos de categorías para Syncfusion TreeView
 * @param {number} props.categoryId - ID de la categoría seleccionada
 * @param {Function} props.onTreeNodeClick - Callback cuando se selecciona un nodo
 * @param {Function} props.onAddSubcategory - Callback para agregar subcategoría
 * @param {boolean} props.showAddSubcategoryButton - Mostrar botón de agregar subcategoría
 * @param {boolean} [props.canCreateSubcategory] - Permiso explícito para crear subcategoría. Si no se envía, se intentará inferir por rol de la empresa seleccionada
 * @param {boolean} props.showCategoryError - Mostrar error de categoría/subcategoría
 * @param {string} props.categoryErrorMessage - Mensaje de error de categoría
 * @param {boolean} props.requireCategory - Si la categoría es requerida (default: false)
 * 
 * @param {Array} props.typeDocuments - Lista de tipos de documentos
 * @param {Object|string} props.selectedTypeDocument - Tipo de documento seleccionado
 * @param {Function} props.onTypeDocumentChange - Callback cuando cambia el tipo de documento
 * @param {boolean} props.showTypeDocError - Mostrar error de tipo de documento
 * @param {string} props.typeDocErrorMessage - Mensaje de error de tipo de documento
 * @param {boolean} props.requireTypeDocument - Si el tipo de documento es requerido (default: false)
 * 
 * @param {number} props.infoType - Tipo de información (1: documento, 2: expediente)
 * @param {Array} props.invalidFields - Campos con errores de validación
 * @param {boolean} props.showCategoryDialog - Mostrar diálogo de categoría
 * @param {Function} props.onCloseCategoryDialog - Callback para cerrar diálogo
 * @param {Function} props.onSubmitCategory - Callback para guardar categoría
 * @param {string} props.categoryName - Nombre de la categoría
 * @param {Function} props.onCategoryNameChange - Callback cuando cambia el nombre
 * @param {boolean} props.categoryNameError - Error en el nombre de categoría
 * @param {string} props.dialogType - Tipo de diálogo ('category' o 'subcategory')
 * @param {Function} props.treeRefCallback - Callback para obtener referencia del TreeView
 * @param {Function} props.onUploadDataChange - Callback para cambios en datos de carga
 * @param {number} props.documentId - ID del documento
 * @param {Function} props.setCustomVariables - Función para establecer variables personalizadas
 * @param {boolean} props.usePanelBar - Usar PanelBar (por defecto true)
 */
const ClassificationPanelPure = ({
  // Empresa
  companies = [],
  companyId,
  onCompanyChange,
  companyDisabled = false,
  showCompanyError = false,
  companyErrorMessage = '',
  requireCompany = false, // Nuevo prop para hacer empresa obligatoria

  // Categorías y Subcategorías (Syncfusion TreeView)
  categoriesData = [],
  categoryId,
  onTreeNodeClick,
  onAddSubcategory,
  showAddSubcategoryButton = true,
  canCreateSubcategory, // Permiso opcional explícito
  expandedNodes = [], // Array de IDs de nodos a expandir
  showCategoryError = false, // Mostrar error de categoría/subcategoría
  categoryErrorMessage = '', // Mensaje de error de categoría
  requireCategory = false, // Si la categoría es requerida

  // Tipo de documento
  typeDocuments = [],
  selectedTypeDocument,
  onTypeDocumentChange,
  showTypeDocError = false,
  typeDocErrorMessage = '',
  requireTypeDocument = false, // Si el tipo de documento es requerido

  // General
  infoType = 1,
  invalidFields = [],
  showCategoryDialog = false,
  onCloseCategoryDialog,
  onSubmitCategory,
  categoryName = '',
  onCategoryNameChange,
  categoryNameError = false,
  dialogType = 'category',
  treeRefCallback,
  onUploadDataChange,
  documentId,
  setCustomVariables,
  selectedNodes = [],

  // Configuración
  usePanelBar = true
}) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState('')
  const [filteredCategories, setFilteredCategories] = useState(categoriesData)
  const [treeKey, setTreeKey] = useState(0)
  const treeRef = useRef(null)
  const didInitExpandRef = useRef(false)

  const handleTreeRef = useCallback((scope) => {
    treeRef.current = scope?.ej2_instances?.[0] || scope
    if (treeRefCallback) treeRefCallback(treeRef.current)
  }, [treeRefCallback])

  const handleSearchChange = (e) => {
    const value = e?.target?.value || ''
    setSearchValue(value)
  }

  const filterTreeBySubcategory = (nodes = [], term = '') => {
    if (!term.trim()) return nodes;

    const lower = term.toLowerCase();

    // Buscar en TODOS los nodos, no solo subcategorías
    const matches = nodes.filter((node) => {
      const nameMatches = node.name && node.name.toLowerCase().includes(lower);
      return nameMatches;
    });

    if (matches.length === 0) {
      return [];
    }

    // Recopilar todos los IDs necesarios (matches + ancestros)
    const allNeededIds = new Set();

    // Función recursiva para agregar todos los ancestros
    const addAllAncestors = (nodeId) => {
      if (!nodeId || nodeId === 0) return;
      allNeededIds.add(nodeId);

      const node = nodes.find(n => n.id === nodeId);
      if (node && (node.parentId || node.parentId === 0)) {
        const parent = Number(node.parentId)
        if (!isNaN(parent) && parent !== 0) addAllAncestors(parent)
      }
    }

    const addAllDescendants = (nodeId) => {
      const children = nodes.filter(n => Number(n.parentId) === Number(nodeId))
      for (const child of children) {
        if (!allNeededIds.has(child.id)) {
          allNeededIds.add(child.id)
          addAllDescendants(child.id)
        }
      }
    };

    matches.forEach(match => {
      allNeededIds.add(match.id);
      if (match.parentId || match.parentId === 0) addAllAncestors(match.parentId)
      addAllDescendants(match.id)
    });

    // Asegurar que el nodo raíz (empresa) siempre esté incluido
    const rootNode = nodes.find(n => n.parentId === null || n.parentId === 0);
    if (rootNode) allNeededIds.add(rootNode.id)

    // Filtrar y MANTENER JERARQUÍA COMPLETA
    const filtered = nodes
      .filter(node => allNeededIds.has(node.id))
      .map(node => ({
        ...node,
        parentId:
          node.parentId === 0 ||
            node.parentId === '0' ||
            node.parentId === null ||
            node.parentId === undefined
            ? null
            : node.parentId,
      }));

    // Mantener el mismo orden original del árbol para que Syncfusion conserve niveles
    const orderedFiltered = nodes.filter(n => filtered.some(f => f.id === n.id));

    return orderedFiltered;

  };

  // Efecto para filtrar categorías

  // Efecto para gestionar expansión sin colapsar el árbol al interactuar
  useEffect(() => {
    if (!treeRef.current || filteredCategories.length === 0) return;

    setTimeout(() => {
      try {
        if (searchValue.trim()) {
          treeRef.current.expandAll();
        } else if (!didInitExpandRef.current) {
          const root = treeRef.current.getNode("0")
          if (root?.hasChildNodes) treeRef.current.expand([root]);
          didInitExpandRef.current = true
        }
      } catch (error) {
        console.log('Error en expansión:', error);
      }
    }, 300);
  }, [filteredCategories, searchValue, treeKey])

  // Efecto para filtrar categorías - CON NORMALIZACIÓN
  useEffect(() => {
    const filtered = filterTreeBySubcategory(categoriesData, searchValue);

    // Normalizar datos para asegurar jerarquía
    const normalizedData = filtered.map(node => ({
      ...node,
      // Asegurar que el nodo principal tenga hasChild: true si tiene hijos
      hasChild: node.hasChild || categoriesData.some(child =>
        child.parentId === node.id || child.parentId === node.id.toString()
      )
    }));

    setFilteredCategories(normalizedData);

    if (searchValue.trim()) {
      setTreeKey(prev => prev + 1);
    }
  }, [searchValue, categoriesData]);


  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  useEffect(() => {
    if (!treeRef.current || !selectedNodes?.length || hasAutoScrolled) return;

    const tree = treeRef.current;
    const targetId = selectedNodes[0]?.toString();

    const timer = setTimeout(() => {
      try {
        const liElements = tree.element.querySelectorAll('li[data-uid]');
        let targetUid = null;

        liElements.forEach(li => {
          const nodeData = tree.getNode(li);
          if (nodeData && nodeData.id?.toString() === targetId) {
            targetUid = li.getAttribute('data-uid');
          }
        });

        if (targetUid) {
          tree.selectedNodes = [targetUid];
          tree.ensureVisible(targetUid);

          setHasAutoScrolled(true);
        } else {
          console.log(`No se encontró UID para la subcategoría ${targetId}`);
        }
      } catch (err) {
        console.log('Error seleccionando subcategoría:', err);
        setHasAutoScrolled(true);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [filteredCategories, selectedNodes, treeKey, hasAutoScrolled]);

  const getCategoryDialogText = (key) => {
    const isCategory = dialogType === 'category'
    const map = {
      saveBtn: isCategory
        ? t('fileManager.createCategory.dialog.saveBtn')
        : t('fileManager.createSubcategory.dialog.saveBtn'),
      cancelBtn: t('fileManager.createCategory.dialog.cancelBtn'),
      header: isCategory
        ? t('fileManager.createCategory.dialog.header')
        : t('fileManager.createSubcategory.dialog.header'),
      errorInputText: isCategory
        ? t('fileManager.createCategory.dialog.errorInputText')
        : t('fileManager.createSubcategory.dialog.errorInputText'),
      categoryInput: isCategory
        ? t('fileManager.createCategory.dialog.categoryInput')
        : t('fileManager.createSubcategory.dialog.categoryInput'),
    }
    return map[key]
  }

  // Inferencia de permisos para crear subcategoría según el rol del usuario en la empresa seleccionada
  const inferredCanCreateSubcategory = useMemo(() => {

    if (typeof canCreateSubcategory === 'boolean') {
      return canCreateSubcategory
    }

    const company = companies?.find?.((c) => c && (c.id?.toString?.() === companyId?.toString?.()))

    if (!company) {
      return true
    }

    const result = canCreateSubcategoryByRole(company, { defaultAllow: true })
    return result
  }, [canCreateSubcategory, companies, companyId])

  const renderTreeView = () => {

    return (
      <TreeViewComponent
        ref={handleTreeRef}
        fields={{
          dataSource: filteredCategories,
          id: 'id',
          parentID: 'parentId',
          text: 'name',
          hasChildren: 'hasChild',
          iconCss: 'filterPath',
        }}
        cssClass='custom-treeview'
        expandOn='Click'
        allowMultiSelection={false}
        nodeSelected={onTreeNodeClick}
        loadOnDemand={false}
        showCheckBox={false}
        selectedNodes={selectedNodes}
      />
    );
  };

  const renderContent = () => (
    <>
      {/* Selector de Empresa */}
      <div>
        <DropDownBtnHLPure
          labelDropDown={t('digitalVaultUploadView.company') || t('questionnairesAssignCategories.company')}
          optionsDropDown={companies}
          optionsValue={companyId}
          valueItem={'id'}
          nameOption={'companyName'}
          onDropDownChange={onCompanyChange}
          required={requireCompany}
          disable={companyDisabled}
          showError={showCompanyError}
          errorText={companyErrorMessage}
        />
      </div>

      {/* Árbol de Categorías y Subcategorías (Syncfusion TreeView) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '10px'
      }}>
        <p className='inputHLPure-label'>
          {t('digitalVaultUploadView.category.subcategory')}
          {requireCategory && <span style={{ color: '#427FBA', marginLeft: '4px' }}>*</span>}
        </p>

        <div style={{ display: 'flex', gap: '8px' }}>
          <SearchBarHLPure
            placeholder={t('classificationPanel.placeholder.search')}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            fullWidth
          />
        </div>
      </div>

      <div id="treeparent">
        <div id="DigitalVault_treeview">
          {renderTreeView()}
        </div>
      </div>

      {/* Mensaje de error para categoría/subcategoría */}
      {showCategoryError && categoryErrorMessage && (
        <div style={{ marginTop: '4px' }}>
          <p className='labelErrorPure-label'>
            {categoryErrorMessage}
          </p>
        </div>
      )}

      {/* Selector de Tipo de Documento */}
      <div style={{ marginTop: '20px' }} className="questionnairesAssignCategories-type-document">
        <ComboBoxTemplatePure
          title={
            infoType === 1
              ? t('digitalVaultUploadView.document.type')
              : infoType === 2
                ? t('digitalVaultUploadView.file.type')
                : t('questionnairesAssignCategories.type.document')
          }
          placeholder={
            infoType === 1
              ? t('digitalVaultUploadView.select.document.type')
              : infoType === 2
                ? t('digitalVaultUploadView.select.file.type')
                : t('questionnairesAssignCategories.select.type.document')
          }
          allowCustom={false}
          data={typeDocuments}
          textField='name'
          dataItemKey='id'
          value={selectedTypeDocument}
          onChange={(e) => {
            onTypeDocumentChange(e)
            if (!documentId && setCustomVariables) {
              setCustomVariables(e.value?.variables || [])
            }
          }}
          validityStyles={false}
          errorCombobox={invalidFields.includes('typeDocument') || showTypeDocError}
          errorMessage={typeDocErrorMessage}
          infoType={infoType}
          required={requireTypeDocument}
        />
      </div>
    </>
  )

  return (
    <>
      {usePanelBar ? (
        <PanelBarGLPure
          expandedPanel={true}
          titlePanel={t('digitalVaultUploadView.modal.clasification.title')}
          className='documentPanelBarVariables'
          id='documentPanelBarVariables'
          actionComponent={
            <div style={{ display: 'flex', gap: '8px' }}>
              {(() => {
                const shouldShow = showAddSubcategoryButton && companyId && categoryId > 0 && inferredCanCreateSubcategory
                return shouldShow ? (
                  <ButtonAddHLPure
                    nameBtn={t('digitalVaultUploadView.button.add.subcategory')}
                    onClickBtn={onAddSubcategory}
                    iconAdd={true}
                  />
                ) : null
              })()}
            </div>
          }
        >
          {renderContent()}
        </PanelBarGLPure>
      ) : (
        renderContent()
      )}

      {showCategoryDialog && (
        <DialogYesNoHLPure
          btnCancel={true}
          grid={true}
          cancel={onCloseCategoryDialog}
          handleCancel={onCloseCategoryDialog}
          handleSubmit={onSubmitCategory}
          saveBtn={getCategoryDialogText('saveBtn')}
          textBtnCancel={getCategoryDialogText('cancelBtn')}
          titleDialog={getCategoryDialogText('header')}
        >
          <InputHLPure
            inputActive={true}
            errorInput={categoryNameError}
            errorInputText={getCategoryDialogText('errorInputText')}
            labelInput={getCategoryDialogText('categoryInput')}
            handleChangeInput={(e) => onCategoryNameChange(e.target.value)}
            typeInput={'text'}
            valueInput={categoryName}
            characterLimit={128}
          />
        </DialogYesNoHLPure>
      )}
    </>
  )
}

export default ClassificationPanelPure
